import {
  TCompanyDefaultsState,
  TFilterPreferencesConfig,
  TPreferencesLayer,
  TPreferencesSnapshot,
} from '@/shared/features/filter-preferences/context/filter-preferences-config-context.types';
import {
  useReadTenantQuery,
  useUpdateTenantMutation,
} from '@/shared/redux/api/ident/ident-api';
import { TenantFragment } from '@/shared/types/types.generated';
import { TTenantUiPreferences } from '@/shared/types/ui-preferences.types';

const toSnapshot = (
  tenant?: Pick<TenantFragment, 'uiPreferences' | 'objectVersion'>,
): TPreferencesSnapshot<TTenantUiPreferences> => ({
  blob: (tenant?.uiPreferences ?? {}) as TTenantUiPreferences,
  version: tenant?.objectVersion ?? null,
});

const useCompanyDefaults = (): TCompanyDefaultsState => {
  const { data, isSuccess, isError } = useReadTenantQuery();
  const uiPreferences = data?.readTenant?.uiPreferences as
    | TTenantUiPreferences
    | undefined;

  // Settled (success or error) counts as ready so a failed tenant read falls
  // through to code defaults instead of hanging every scoped list.
  return {
    filterDefaults: uiPreferences?.filterDefaults,
    isReady: isSuccess || isError,
  };
};

const useCompanyWriter = (): TPreferencesLayer<TTenantUiPreferences> => {
  const { refetch } = useReadTenantQuery();
  const [updateTenant] = useUpdateTenantMutation();

  return {
    readFresh: async () => {
      const fresh = await refetch().unwrap();
      return toSnapshot(fresh?.readTenant);
    },
    submit: async ({ blob, version }) => {
      await updateTenant({
        tenant: { uiPreferences: blob, objectVersion: (version ?? 0) + 1 },
      }).unwrap();
    },
  };
};

export const consoleFilterPreferencesConfig: TFilterPreferencesConfig = {
  useCompanyDefaults,
  useCompanyWriter,
  companyDefaultAction: 'ident:updateTenant',
};
